import type {
  ProposalActivity,
  ProposalCollaborationEnvelope,
  ProposalReviewerChange,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { isReviewerApprovalCurrent } from "work/proposals/negotiation/proposal-reviewer-approval-state";

/**
 * What the submit dialog says when the leader's own reviewers have not finished with what is about to
 * be sent.
 *
 * This warning went out with the review-lock nag (d8016a18e): the leader was told on every submit that
 * a reviewer's approval had gone non-current, by a confirmation that reworded its own question into
 * "do you still want to submit?" and offered no way to resolve it. Submitting is unconditional now and
 * stays that way — this is a line above the change list, not a gate, and the buttons read the same
 * either way. What it restores is agreement: the header's approval badge has gone on counting pending
 * reviews beside the Submit button all along, and a dialog that said nothing while the badge said
 * "2 reviews pending" left the leader to reconcile the two (#5128).
 *
 * "Pending" is therefore the badge's own definition — no CURRENT approval — so the two cannot
 * disagree. A reviewer who approved and then had the proposal edited under them is pending again,
 * because what they approved is not what this submission sends.
 */

/**
 * Reviewer changes the leader has staged and not saved yet. A reviewer staged for removal is one whose
 * review the leader has already decided does not count, and Approve and Approve & Hire save the removal
 * before they act (#6869). The warning and the popover leave that reviewer out, rather than count and
 * name one who is about to go.
 */
export type StagedProposalReviewerChanges = readonly ProposalReviewerChange[];

/** The viewing party's own reviewers who have no current approval of the state being submitted. */
export function countPendingProposalReviews(
  envelope: ProposalCollaborationEnvelope,
  stagedReviewerChanges: StagedProposalReviewerChanges = [],
): number {
  return pendingProposalReviewers(envelope, stagedReviewerChanges).length;
}

/**
 * Why a reviewer is still pending: never reviewed, approved a version this one has replaced, made the
 * latest changes and has not finalized (#6868), or not sent the proposal yet — a reviewer carried onto
 * a revision, who is told about it only once the leader sends it, and so is never reminded about it.
 */
export type PendingProposalReviewStatus = "notReviewed" | "approvedEarlierVersion" | "notFinalized" | "notSentYet";

/** Whether a pending reviewer can be reminded: one who was sent the proposal and has not reviewed it. */
export function isRemindable(reviewer: PendingProposalReviewer): boolean {
  return reviewer.status !== "notSentYet";
}

export type PendingProposalReviewer = {
  userId: string;
  legalEntityId: string;
  status: PendingProposalReviewStatus;
};

type RosterReviewer = { userId: string; legalEntityId: string; isAwaitingReviewRequest?: boolean | null };

/**
 * The reviewers `countPendingProposalReviews` counts, by name rather than by number (#6618), with why
 * each is pending. An approval the envelope still records, with the date it was given, but no longer
 * calls current is an earlier version's, and asking that reviewer again is a re-review request. A
 * re-review request leaves the reviewer's entry in place with no date, because it dismissed the
 * approval; that reviewer, like anyone with no entry, has not reviewed what is there now.
 *
 * A reviewer who made the latest change has seen it: they wrote it. What they have not done is
 * finalize, so that is what they are told and reminded about (#6868). It holds only while their change
 * is the latest. Once someone else on the party changes the proposal after them, or either party
 * submits, there is something they have not seen, and they have "not reviewed" again.
 *
 * The roster is the one the next save leaves: a reviewer staged for removal is not listed (#6869).
 */
export function pendingProposalReviewers(
  envelope: ProposalCollaborationEnvelope,
  stagedReviewerChanges: StagedProposalReviewerChanges = [],
): PendingProposalReviewer[] {
  const reviewers: RosterReviewer[] =
    envelope.viewingParty === "Vendor"
      ? envelope.projection.vendorReviewers
      : envelope.projection.clientReviewers;
  const latestChange = latestPartyChangeSinceSubmission(envelope);
  // The envelope's approvals, not the projection's `dateApproved`: that field is snapshot metadata
  // which a later submission leaves stale, which is why `applyCurrentReviewerApprovals` exists.
  return reviewers
    .filter((reviewer) => !isStagedForRemoval(reviewer, stagedReviewerChanges))
    .filter((reviewer) => !isReviewerApprovalCurrent(envelope, reviewer))
    .map((reviewer) => ({
      userId: reviewer.userId,
      legalEntityId: reviewer.legalEntityId,
      status: pendingStatus(envelope, reviewer, latestChange),
    }));
}

function pendingStatus(
  envelope: ProposalCollaborationEnvelope,
  reviewer: RosterReviewer,
  latestChange: ProposalActivity | undefined,
): PendingProposalReviewStatus {
  if (reviewer.isAwaitingReviewRequest) return "notSentYet";
  if (madeChange(latestChange, reviewer)) return "notFinalized";

  const approvedBefore = envelope.reviewerApprovals.some(
    (approval) =>
      approval.party === envelope.viewingParty &&
      approval.reviewerUserId === reviewer.userId &&
      approval.reviewerLegalEntityId === reviewer.legalEntityId &&
      Boolean(approval.approvedDate),
  );
  return approvedBefore ? "approvedEarlierVersion" : "notReviewed";
}

function madeChange(change: ProposalActivity | undefined, reviewer: RosterReviewer): boolean {
  if (!change || change.actorUserId !== reviewer.userId) return false;
  // An activity from an older API may not name the legal entity; the user alone then decides.
  return !change.actorLegalEntityId || change.actorLegalEntityId === reviewer.legalEntityId;
}

/**
 * The kinds that change what the proposal says: an edit, and a decision to take or keep a value.
 * Comments, roster changes and reviews change nothing a reviewer has to look at.
 */
const PROPOSAL_CHANGE_KINDS: ReadonlySet<ProposalActivity["kind"]> = new Set([
  "FieldSet",
  "CollectionItemAdded",
  "CollectionItemUpdated",
  "CollectionItemRemoved",
  "ResolutionDecisionRecorded",
]);

/**
 * The viewing party's latest change since the last submission by either party, which is the
 * checkpoint. Its `actorUserId` is who made it. The reminder the server sends applies the same rule to
 * the event stream (`ProposalReviewerFinalization`), so the row and the reminder agree.
 */
function latestPartyChangeSinceSubmission(envelope: ProposalCollaborationEnvelope): ProposalActivity | undefined {
  const checkpoint = Math.max(
    0,
    ...envelope.recentActivity.filter((item) => item.kind === "SubmissionCreated").map((item) => item.sequence),
  );
  let latest: ProposalActivity | undefined;
  for (const item of envelope.recentActivity) {
    if (item.sequence <= checkpoint) continue;
    if (item.party !== envelope.viewingParty || !PROPOSAL_CHANGE_KINDS.has(item.kind)) continue;
    if (!latest || item.sequence > latest.sequence) latest = item;
  }
  return latest;
}

function isStagedForRemoval(reviewer: RosterReviewer, stagedReviewerChanges: StagedProposalReviewerChanges): boolean {
  // The last staged change decides: a reviewer removed and then added back stays on the roster.
  const lastChange = stagedReviewerChanges.findLast(
    (change) => change.reviewerUserId === reviewer.userId && change.reviewerLegalEntityId === reviewer.legalEntityId,
  );
  return lastChange?.kind === "Remove";
}

/**
 * The sentence, or nothing at all when every reviewer has approved this exact state — a proposal with
 * no reviewers included, which has no review to be pending.
 *
 * The sentence names the count and nothing else (#5560). It said who the submission reaches and that
 * it goes without their review, which the dialog's own heading and button already say; the one fact
 * the leader does not have is that their reviewers have not seen the latest changes. So the audience
 * is no longer a parameter here — the dialog still names it elsewhere.
 *
 * A reviewer who made the latest change has seen it, so they are counted apart, as not having
 * finalized their review (#6868). When both kinds are pending, the warning says both sentences.
 */
export function proposalPendingReviewWarning(
  envelope: ProposalCollaborationEnvelope,
  stagedReviewerChanges: StagedProposalReviewerChanges = [],
): string | undefined {
  const pending = pendingProposalReviewers(envelope, stagedReviewerChanges);
  const notFinalized = pending.filter((reviewer) => reviewer.status === "notFinalized").length;
  const sentences = [
    pendingProposalReviewWarning(pending.length - notFinalized),
    unfinalizedReviewWarning(notFinalized),
  ].filter((sentence) => sentence !== undefined);
  return sentences.length > 0 ? sentences.join(" ") : undefined;
}

function unfinalizedReviewWarning(count: number): string | undefined {
  // One change is the latest, so at most one reviewer made it: there is no plural to write.
  return count === 0 ? undefined : "1 reviewer hasn't finalized their review.";
}

/**
 * The same sentence from a count alone, for the one submission that has no envelope to count from: a
 * draft nobody has saved yet, asked before anything is written (#5666). Nothing is persisted, so no
 * reviewer on it can hold an approval — every reviewer the leader picked is pending.
 */
export function pendingProposalReviewWarning(pendingReviews: number): string | undefined {
  if (pendingReviews === 0) return undefined;

  const reviewers = pendingReviews === 1 ? "1 reviewer has" : `${pendingReviews} reviewers have`;
  // "The" latest changes, not "your": Approve and Hire show this sentence too (#6635), and the terms
  // they commit to are often the other party's.
  return `${reviewers} not reviewed the latest changes.`;
}
